import {trans} from '@frontend/helpers'
import {
    useRegionConfig,
    useUserData,
} from '@frontend/hooks'
import {ExecutorDetailsSendMessageModal} from '@frontend/modules/public/services/components/details/ExecutorDetailsSendMessageModal'
import {PublicExecutorsApi} from '@frontend/modules/public/services/PublicExecutorsApi'
import type {
    ExecutorDetailsData,
    ExecutorPreviewData,
} from '@frontend/modules/public/services/ServicesModuleTypes'
import {
    useAuthStore,
    useMiniChatStore,
} from '@frontend/stores'
import {
    mdiCellphoneText,
    mdiMessageReplyText,
} from '@mdi/js'
import clsx from 'clsx'
import {useState} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {formatValueForMask} from 'swayok-react-mdb-ui-kit/components/Input/MaskedNumericInput'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'

interface Props<T> {
    executor: T
    className?: string
    modalDepth?: number
    // Показывать в кнопках только иконки. Кнопки при этом круглые.
    onlyIcons?: boolean
    // Уменьшить размер кнопок и надписей.
    condensed?: boolean
    disabled?: boolean
    onExecutorDataChange?: (executor: T) => void
    onSendMessageButtonClick?: () => void
}

// Кнопки контакта с продавцом.
export function ExecutorCommunicationButtons<
    T extends ExecutorPreviewData | ExecutorDetailsData = ExecutorDetailsData,
>(props: Props<T>) {

    const {
        executor,
        className,
        modalDepth,
        onlyIcons,
        condensed = false,
        disabled,
        onExecutorDataChange,
        onSendMessageButtonClick,
    } = props

    const user = useUserData()
    const isLoggedIn: boolean = !!user
    const regionConfig = useRegionConfig()

    const openChat = useMiniChatStore(
        state => state.openChat
    )
    const initUserAuthModal = useAuthStore(
        state => state.initUserAuthModal
    )

    // Состояние запроса данных из API.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Видимость модального окна отправки сообщения продавцу.
    const [
        isMessageModalVisible,
        setIsMessageModalVisible,
    ] = useState<boolean | null>(null)

    // Показать номер телефона.
    const showPhone = (): void => {
        if (isProcessing) {
            return
        }
        if (executor.phone) {
            // Вызываем приложение вызова на устройстве.
            window.location.assign('tel:' + executor.phone.replace(/[^0-9+]/g, ''))
            return
        }
        if (!isLoggedIn) {
            // Не авторизован: открываем форму авторизации.
            // После успешной авторизации повторяем вызов функции.
            initUserAuthModal((): void => {
                // Тайм-аут нужен на получение обновленных данных объявления.
                setTimeout(() => {
                    showPhone()
                }, 1000)
            })
            return
        }
        setIsProcessing(true)
        PublicExecutorsApi.getExecutorPhone(executor.id)
            .then((phone: string) => {
                onExecutorDataChange?.({...executor, phone})
                if (onlyIcons) {
                    // Вызываем приложение вызова на устройстве.
                    window.location.assign('tel:' + phone.replace(/[^0-9+]/g, ''))
                }
            })
            .catch((error: ApiError) => {
                handleErrorResponse(error)
            })
            .finally(() => {
                setIsProcessing(false)
            })
    }

    // Пользователь хочет отправить сообщение продавцу.
    const initMessageSending = (): void => {
        if (executor.currentUser.isOwner) {
            return
        }
        onSendMessageButtonClick?.()
        if (!isLoggedIn) {
            // Не авторизован: открываем форму авторизации.
            // После успешной авторизации повторяем вызов функции.
            initUserAuthModal((): void => {
                // Тайм-аут нужен на получение обновленных данных объявления.
                setTimeout(() => {
                    initMessageSending()
                }, 1000)
            })
            return
        }
        if (executor.currentUser.chatId) {
            // Чат уже существует: открываем его в мини-чате.
            openChat(executor.currentUser.chatId)
            return
        }
        setIsMessageModalVisible(true)
    }

    // Сообщение отправлено.
    const onMessageSent = (chatId: number) => {
        // Переходим к чату с продавцом в мини-чате.
        openChat(chatId)
        setIsMessageModalVisible(false)
        const updates = {...executor}
        updates.currentUser = {...executor.currentUser, chatId}
        onExecutorDataChange?.(updates)
    }

    const buttonClassName: string = clsx(
        onlyIcons
            ? 'ms-3 p-3 with-icon-flex rounded-circle lh-1'
            : 'full-width mt-3 mt-md-4 flex-row-between rounded-8',
        condensed ? 'button-condensed' : null
    )

    return (
        <div
            className={clsx(
                'executor-details-menu-buttons',
                className
            )}
        >
            {executor.communicationMethods.messages && (
                <Button
                    color="blue"
                    className={buttonClassName}
                    disabled={executor.currentUser.isOwner || disabled}
                    onClick={initMessageSending}
                    title={
                        executor.currentUser.isOwner
                            ? trans().ads.details.this_is_your_ad
                            : undefined
                    }
                >
                    {!onlyIcons && (
                        <span>
                            {
                                executor.currentUser.chatId
                                    ? trans().services.details.open_chat_with_executor(condensed)
                                    : trans().services.details.contact_seller_via_messages
                            }
                        </span>
                    )}
                    <Icon
                        path={mdiMessageReplyText}
                        className={onlyIcons ? '' : 'ms-3'}
                    />
                </Button>
            )}

            {executor.communicationMethods.phone && (
                <Button
                    color="green"
                    className={buttonClassName}
                    onClick={showPhone}
                    disabled={isProcessing || disabled}
                >
                    {!onlyIcons && (
                        <span>
                            {
                                executor.phone
                                    ? formatValueForMask(executor.phone, regionConfig.phone.template, false)
                                    : trans().services.details.show_seller_phone(condensed)
                            }
                        </span>
                    )}
                    <Icon
                        path={mdiCellphoneText}
                        className={onlyIcons ? '' : 'ms-3'}
                    />
                </Button>
            )}
            {user && !executor.currentUser.isOwner && (
                <ExecutorDetailsSendMessageModal
                    show={isMessageModalVisible}
                    depth={modalDepth}
                    executorId={executor.id}
                    onClose={() => {
                        setIsMessageModalVisible(false)
                    }}
                    onClosed={() => {
                        setIsMessageModalVisible(null)
                    }}
                    onMessageSent={onMessageSent}
                />
            )}
        </div>
    )
}
